//
// Lightweight, advisory name-similarity matching for the "looks like an existing field" hint.
//
// ADVISORY only: the hard, blocking uniqueness guard is the server-side exact-match check
// (KPI.nameExists). This just surfaces likely duplicates as the user types a name.
//
// Approach: Sorensen-Dice coefficient over character bigrams of the accent-folded, normalised
// names. It is a standard, dependency-free fuzzy metric: symmetric, tolerant of typos and minor
// word-order differences, and (unlike the previous substring/shared-token rule) it does not match
// every field that merely shares a single letter or common word. A whole-word / substring match is
// treated as a strong signal. A minimum normalised query length plus a similarity threshold keep
// junk out (e.g. a stray single character, or non-Latin input that normalises to almost nothing).
//

/** Fold accents/diacritics so "Doorlooptíjd" matches "doorlooptijd". */
const foldAccents = (value: string): string => value.normalize('NFD').replace(/\p{Diacritic}/gu, '');

/** Normalise a name: accent-fold, lower-case, collapse every non-alphanumeric run to a single space. */
export const normalizeName = (value: string): string =>
  foldAccents(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** Meaningful tokens of a name (>= 3 chars), used for whole-word matching. */
export const nameTokens = (value: string): string[] =>
  normalizeName(value)
    .split(' ')
    .filter((token) => token.length >= 3);

/** Adjacent character bigrams of a string (spaces removed), as a count map. */
const bigrams = (value: string): Map<string, number> => {
  const compact = value.replace(/ /g, '');
  const map = new Map<string, number>();
  for (let i = 0; i < compact.length - 1; i++) {
    const gram = compact.slice(i, i + 2);
    map.set(gram, (map.get(gram) ?? 0) + 1);
  }

  return map;
};

/** Sorensen-Dice coefficient (0..1) over character bigrams. */
const diceCoefficient = (a: string, b: string): number => {
  const ba = bigrams(a);
  const bb = bigrams(b);
  let totalA = 0;
  let totalB = 0;
  ba.forEach((n) => (totalA += n));
  bb.forEach((n) => (totalB += n));
  if (totalA === 0 || totalB === 0) return 0;

  let intersection = 0;
  ba.forEach((countA, gram) => {
    const countB = bb.get(gram);
    if (countB) intersection += Math.min(countA, countB);
  });

  return (2 * intersection) / (totalA + totalB);
};

// A containment match (one name is a >= 4-char word or substring of the other) is a strong signal.
// The 4-char floor stops a 1-3 char fragment from matching every field that happens to contain it.
const MIN_CONTAIN_LEN = 4;
const CONTAIN_SCORE = 0.9;

const isContained = (hayTokens: string[], hayNorm: string, needleNorm: string): boolean => {
  if (needleNorm.length < MIN_CONTAIN_LEN) return false;
  if (hayTokens.includes(needleNorm)) return true; // exact whole word

  return hayNorm.includes(needleNorm); // substring of >= 4 chars
};

/**
 * Similarity of two names, 0..1.
 * 1.0  identical (normalised)
 * 0.9  one name contains the other as a >= 4-char whole word or substring
 * else Sorensen-Dice bigram coefficient
 */
export const matchScore = (a: string, b: string): number => {
  const na = normalizeName(a);
  const nb = normalizeName(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1;

  if (isContained(nameTokens(b), nb, na) || isContained(nameTokens(a), na, nb)) return CONTAIN_SCORE;

  return diceCoefficient(na, nb);
};

export interface SimilarNameMatch<T> {
  item: T;
  score: number;
}

// Don't suggest until the typed name has enough signal, and only surface genuinely similar names.
const MIN_QUERY_LEN = 3;
const SIMILARITY_THRESHOLD = 0.5;

/**
 * Return up to `limit` items whose name is similar to `name`, best match first.
 * `getName` extracts the comparable name from each candidate. Only names at or above the similarity
 * threshold (or a whole-word containment) are returned, so unrelated fields are never suggested.
 * A name identical to a candidate (normalised) is always returned, whatever its length.
 */
export const findSimilarByName = <T>(
  name: string,
  candidates: T[],
  getName: (item: T) => string,
  limit = 4,
): SimilarNameMatch<T>[] => {
  // Guard on the NORMALISED length: non-Latin or punctuation-only input collapses to near-nothing
  // and must not produce matches (this was the source of the "matches everything" bug).
  // An exact match is never noise, so a short name still surfaces the field it duplicates;
  // without this a 1-2 char duplicate ("t1") shows the blocking error with no reuse offer.
  const normalized = normalizeName(name);
  if (normalized.length < MIN_QUERY_LEN) {
    if (!normalized) return [];

    return candidates
      .filter((item) => normalizeName(getName(item)) === normalized)
      .map((item) => ({ item, score: 1 }))
      .slice(0, limit);
  }

  return candidates
    .map((item) => ({ item, score: matchScore(name, getName(item)) }))
    .filter((match) => match.score >= SIMILARITY_THRESHOLD)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
};
